import React from "react";
import {
  Briefcase,
  Sheet,
  LayoutDashboard,
  ShoppingCart,
  User,
  UserCog,
  Folder,
  ContactRound,
  LockKeyhole,
  Wallet,
  GalleryHorizontalEnd,
  ReceiptText,
  DollarSign,
  HandshakeIcon,
  BanknoteArrowDown,
  Presentation,
  ListTodo,
  ListChecks,
  Building,
  Contact,
  FolderArchive,
  Users,
  Cpu,
  Warehouse,
  PackageCheck,
  Printer,
  CalendarDays,
  Palette,
  Plus,
  Bell,
  Globe,
  Workflow,
  Stamp,
  BadgeCheck,
  Gavel,
  ClipboardCheck,
  Headset,
  FolderKanban,
  ScrollText,
  ArrowRightLeft,
  Scale,
  Coins,
  NotebookPen,
  HandCoins,
  Palmtree,
  UsersRound,
  ShieldCheck,
  History,
  Sparkles,
  ClipboardList,
  Factory,
  Waypoints,
  BellRing,
  Megaphone,
  Repeat,
  Tags,
  Table2,
  ChartColumnBig,
  PiggyBank,
} from "lucide-react";

/**
 * Catálogo de apresentação do menu — fonte única de ícone e descrição por id.
 *
 * O backend é dono de *quais* menus existem (autorização/ordem); este catálogo
 * é dono de *como* cada menu se apresenta. Sidebar, Menu (hub) e SubMenuDashboard
 * consomem daqui, em vez de manter mapas próprios e divergentes.
 *
 * O mapa cobre ids de cabeçalho e de submenu indistintamente: ambos chegam com
 * o mesmo `id` vindo do backend.
 */
const ICON_MAP: Record<string, React.ElementType> = {
  aprovações: ClipboardCheck,
  aprovacoes: Gavel,
  atendimento: Briefcase,
  job: Folder,
  jobs: FolderKanban,
  planejamento: Presentation,
  "jobs-planejamento": Folder,
  "agenda-planejamento": CalendarDays,
  "planilhas-atendimento": Sheet,
  "atendimento-clientes": Building,
  "atendimento-contatos": Contact,
  "pre-producao-contatos": Contact,
  "compras-contatos": Contact,
  facilities: Warehouse,
  "facilities-envios": PackageCheck,
  "agenda-atendimento": CalendarDays,
  "produtividade-atendimento": ChartColumnBig,
  "agenda-pre-producao": CalendarDays,
  "planilhas-atendimento-faturamento": ReceiptText,
  creditaveis: HandCoins,
  dashboard: LayoutDashboard,
  "pré-produção": Sheet,
  "planilhas-pre-producao": Sheet,
  "listagem-versoes": GalleryHorizontalEnd,
  compras: ShoppingCart,
  "planilhas-compras": Sheet,
  "producao-financeiro": Wallet,
  "producao-financeiro-aps": ScrollText,
  "producao-financeiro-conciliacoes": ArrowRightLeft,

  financeiro: DollarSign,
  "custo-fixo": ReceiptText,
  "custo-fixo-gestao": Repeat,
  "custo-fixo-cadastros": Tags,
  "custo-fixo-fechamento": Table2,
  "custo-fixo-consolidacao": ChartColumnBig,
  "custo-fixo-planejamento": PiggyBank,
  "custo-fixo-lancamentos": NotebookPen,
  "custos-projeto": Coins,
  "despesas-projeto": BanknoteArrowDown,
  "aprovacao-despesas": Stamp,
  "aprovacao-comissoes": BadgeCheck,
  "aprovacao-orcamento": Wallet,
  controladoria: Scale,
  comissoes: HandshakeIcon,
  producao: Factory,
  "planilhas-producao": Sheet,
  perfil: User,
  "dados-pessoais": ContactRound,
  notificacoes: Bell,
  ferias: Palmtree,
  "comunicados-historico": Megaphone,
  tarefas: ListTodo,
  "minhas-tarefas": ClipboardList,
  "tarefas-publicas": Globe,
  "criar-tarefa": Plus,
  admin: UserCog,
  permissoes: ShieldCheck,
  cargos: LockKeyhole,
  usuarios: Users,

  "dashboard-atendimento": Headset,
  "dashboard-pre-producao": Sheet,
  "dashboard-compras": ShoppingCart,
  "dashboard-financeiro": ReceiptText,

  "tarefas-admin": ListChecks,
  clientes: Building,
  squads: UsersRound,
  arquivos: FolderArchive,
  "aprovacoes-config": Workflow,
  "aprovacoes-historico": History,
  "processamento-ia": Sparkles,
  "servicos-externos": Waypoints,
  "notificacoes-admin": BellRing,
  "comunicados-admin": Megaphone,
  tecnologia: Cpu,
  "tecnologia-jobs": Folder,
  "agenda-tecnologia": CalendarDays,
  criacao: Palette,
  "criacao-jobs": Folder,
  "producao-grafica": Printer,
  "planilhas-producao-grafica": Sheet,
};

const DEFAULT_ICON = LayoutDashboard;

/**
 * Descrições exibidas nos cards de módulo (Menu) e de funcionalidade
 * (SubMenuDashboard). A sidebar não usa descrição.
 */
const DESCRIPTION_MAP: Record<string, string> = {
  aprovações: "Aprovar versões pendentes e revisar status.",
  job: "Painel principal dos trabalhos ativos.",
  planejamento: "Painel principal para planejamento de jobs.",
  atendimento: "Criação e acompanhamento de jobs em andamento.",
  "pré-produção": "Gerencie as etapas antes da produção.",
  compras: "Controle de planilhas e solicitações de compra.",
  facilities: "Organização interna e envios para clientes.",
  producao: "Despesas extraordinárias dos jobs da sua equipe.",
  "despesas-projeto": "Despesas extraordinárias e seus status de aprovação.",
  "aprovacao-despesas": "Despesas que aguardam a sua decisão.",
  "aprovacao-comissoes": "Comissões que aguardam a sua decisão.",
  "aprovacao-orcamento": "Orçamentos anuais que aguardam a sua decisão.",
  controladoria: "Comissões e controles da Controladoria.",
  comissoes: "Comissões lançadas e seus status de aprovação.",
  perfil: "Atualize suas informações pessoais e preferências.",
  "dados-pessoais": "Seus dados de cadastro e contato.",
  notificacoes: "Escolha por onde receber os avisos do sistema.",
  ferias: "Avise quando estiver fora e defina seu substituto.",
  "comunicados-historico": "Todos os avisos já publicados para você, inclusive os encerrados.",
  permissoes: "Configure funções e permissões de acesso.",
  tarefas: "Gerencie e acompanhe suas tarefas.",
  "minhas-tarefas": "Tarefas atribuídas, criadas e observadas por você.",
  "tarefas-publicas": "Tarefas públicas dos jobs a que você tem acesso.",
  "criar-tarefa": "Crie uma tarefa vinculada a um job e atribua a um responsável.",
  admin: "Ferramentas administrativas do sistema.",
  "aprovacoes-config": "Defina quem aprova cada processo e em que ordem.",
  "aprovacoes-historico": "Consulte o histórico de aprovações e decisões dos fluxos.",
  "servicos-externos": "Teste as integrações externas (e-mail, WhatsApp).",
  "notificacoes-admin": "Histórico de envios de notificações e métricas.",
  "comunicados-admin": "Publica avisos globais (barra, sino e mural) para os usuários.",
  dashboard: "Gráficos, métricas e estatísticas gerais.",
  "producao-financeiro": "Controle financeiro da produção.",
  financeiro: "Gerencie e acompanhe solicitações de faturamento.",
  "custo-fixo": "Feche o mês de cada área, acompanhe o ano e cadastre o que a empresa paga sempre.",
  "custo-fixo-gestao": "O que a empresa paga de forma repetida — e as parcelas de cada um.",
  "custo-fixo-cadastros": "Centros de custo, naturezas e os custos contratáveis.",
  // O fechamento **não tem aprovação** desde a §3.6.3 do conceito: o responsável fecha direto, e
  // corrigir depois é reabrir. O texto continuava prometendo um passo que foi removido de
  // propósito. E "sua área" no singular quebra o §3.2 — quem cuida de cinco lê a frase errada.
  "custo-fixo-fechamento": "Confira os custos do mês das suas áreas e feche o mês.",
  "custo-fixo-consolidacao":
    "Como o ano correu: quanto custou, se coube no orçamento e para onde o dinheiro foi.",
  "custo-fixo-planejamento": "Orçado e gasto do ano, por centro de custo.",
  "custo-fixo-lancamentos": "Preencha os custos fixos por mês.",
  "custos-projeto": "Despesas e comissões de projeto e seus status.",

  jobs: "Acompanhamento e listagem geral de jobs.",
  "planilhas-atendimento": "Controle de orçamentos e planilhas.",
  "atendimento-clientes": "Listagem de clientes e acesso ao Painel 360.",
  "atendimento-contatos":
    "As pessoas importantes dos clientes, com os dados de contato sempre conferidos.",
  "pre-producao-contatos":
    "As pessoas importantes dos clientes, com os dados de contato sempre conferidos.",
  "compras-contatos":
    "As pessoas importantes dos clientes, com os dados de contato sempre conferidos.",
  "facilities-envios":
    "Gifts pedidos pelo Atendimento, para preparar, enviar e marcar como concluídos.",
  "planilhas-atendimento-faturamento": "Solicitações de faturamento do atendimento.",
  creditaveis: "Gestão de créditos por cliente.",
  "jobs-planejamento": "Planejamento e alocação de jobs.",
  "agenda-planejamento": "Agenda de marcos dos jobs do planejamento.",
  "planilhas-pre-producao": "Orçamentos de pré-produção.",
  "listagem-versoes": "Controle de versões de entrega.",
  "agenda-atendimento": "Agenda de marcos dos jobs do atendimento.",
  "produtividade-atendimento": "O que você produziu no período: números, proporções e tendência.",
  "agenda-pre-producao": "Agenda de marcos dos jobs da pré-produção.",
  "tecnologia-jobs": "Acompanhamento e listagem de jobs de tecnologia.",
  criacao: "Acompanhamento dos jobs em criação.",
  "criacao-jobs": "Acompanhamento e listagem de jobs de criação.",
  "agenda-tecnologia": "Agenda de marcos dos jobs de tecnologia.",
  "planilhas-compras": "Gestão de orçamentos de compras.",
  "producao-financeiro-aps": "Aprovação de pagamentos.",
  "producao-financeiro-conciliacoes": "Conciliação de contas da produção.",
};

/** Ids que exibem o badge de notificação de aprovações. */
export const APROVACAO_MENU_IDS = ["aprovações", "aprovacoes"];

/**
 * Ids dos submenus de aprovação — chaves do mapa de contagem por rota (badges do menu).
 * Precisam bater com os ids emitidos pelo get-menus do backend.
 */
export const APROVACAO_SUBMENU_IDS = {
  ORCAMENTOS: "aprovacoes",
  DESPESAS: "aprovacao-despesas",
  COMISSOES: "aprovacao-comissoes",
} as const;

/** Componente de ícone (ElementType) para o id — para quem renderiza `<Icon .../>`. */
export const getMenuIcon = (id: string): React.ElementType => ICON_MAP[id] || DEFAULT_ICON;

/** Ícone já instanciado como elemento — para quem espera `React.ReactNode` (ex.: ModuleCard). */
export const getMenuIconElement = (id: string): React.ReactElement => {
  const Icon = getMenuIcon(id);
  return <Icon />;
};

/** Descrição do menu, com fallback para o nome quando não houver entrada no catálogo. */
export const getMenuDescription = (id: string, fallbackNome?: string): string =>
  DESCRIPTION_MAP[id] || (fallbackNome ? `Acesso ao módulo ${fallbackNome}` : "");
